import {Observable} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";

import {Injectable, ApplicationRef} from "@angular/core";
import {ClientConfiguration} from "../../services/configuration/client.config";
import {Observer} from "rxjs";
import {ContentService} from "../content/content.service";
import {ContentCursor} from "../content/content.cursor";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {RecordingComparisonService, IRecordingComparisonResult, IComparison, PathElementData}
    from "../recording/recording-comparison.service";
import {RecognizerService, IRecognizedSequence} from "../recognition/recognizer.service";
import {CssSentence, CssWord} from "../../content/content-view.objects";
import {IExportDictionary, Pronunciation, IMetaData, ISentence} from "../../api/data-model";
import {ReadToMeService} from "../reading/readtome.service";
import {SessionService} from "../configuration/session.service";
import {MicrophoneStatus} from "../audio/microphone.service";
import {RecordingService, RecordingServiceStatus} from "../recording/recording.service";
import {MockMicrophoneService} from "../audio/mockmicrophone.service";
import {AudioFormatService} from "../audio/audioformat.service";
import {MicrophoneCheckData} from "../recording/recording.objects";
import {CssSelection} from "../../content/content-view.objects";
import {BrowserAgent} from "../browser/browser.agent";
import {ModalConfig} from "../notification/modal.config";
import {EventBus} from "../../event/EventBus";
import {StudentConfiguration} from "../assignment/student.configuration";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {share} from "rxjs/operators";

export enum RuntimeCheck {
    TEST_METADATA = 1,
    TEST_SENTENCE = 2,
    TEST_REFERENCE = 3,
    TEST_DICTIONARY = 4,
    TEST_READY = 10,
    TEST_FINISHED_FILE = 11, // finished processing one file (audio processing)
    TEST_FINISHED_COMPARE = 12, // finished comparing recognition result to transcription
    TEST_FINISHED_ALL = 20 // finished test set
}

interface IRuntimeTestData {
    [index:string]:MockCssSentence;

}
interface IRuntimeTruthData {
    [index: string]: IRecognizedSequence;

}

interface IResults {
    [index: string]: IResult;

}

interface IResult {
        testResult: string;
        referenceResult: string;
        numCorrectWords:number;       // could call this "true positive" - user said a word right,
                                      // and we thought they said it right
        numTrueNegativeWords: number; // true negative = user said a word wrong, and identified it as an error
        numFalsePositiveWords:number; // false positive = user said a word wrong, but we didn't identify it as an error
        numFalseNegativeWords:number; // false negative = user said a word right, but we identified it as an error

        testResultDetail:PathElementData[];
        referenceResultDetail:PathElementData[];
}

interface ITestData {
    sentence: any; // depending on usage, could be grammar or recognized sequence
    audioFile: string;
}

class MockCssSentence extends CssSentence {

    public pronunciationDictionary:IExportDictionary;
    public dictionary():IExportDictionary {
        return this.pronunciationDictionary;
    }
}

@Injectable()
export class RuntimeTestService {

    private statusString;

    public automatedPath:string = "automated/";
    public testPath:string = "automated/mockdata/";

    public runtimeTestServiceStatus:Observable<RuntimeCheck>;
    private _runtimeTestServiceStatusObserver:Observer<RuntimeCheck>;

    private actualSentenceDataSet:IRuntimeTestData = {};
    private recordedSentenceDataSet:IRuntimeTruthData  = {};
    private dictionary:IExportDictionary= {};
    private currentTestItem:ITestData = null;
    private testAudioList:Array<string> = [];
    private results:IResults = null;
    private start:Date;
    private audioSec:number = 0;
    private processingSec:number = 0;

    private currentTestStatus:number = 0;

    private TEST_CONTENT_PATH:string = "https://s3-us-west-1.amazonaws.com/racontent/";

    private recordingService:RecordingService = null;
    private mockMicrophoneService:MockMicrophoneService = null;
    private contentCursor:ContentCursor = null;
    private studentConfig:StudentConfiguration = null;
    private referenceRecordingComparisonService = null;

    constructor(private log:BugsnagLoggingService,
                private clientConfiguration:ClientConfiguration,
                private contentService:ContentService,
                private readToMeService:ReadToMeService,
                private sessionService:SessionService,
                private audioFormat: AudioFormatService,
                private eventBus: EventBus,
                private ref:ApplicationRef,
                private http:HttpClient,
                private browserAgent:BrowserAgent,
                private modalConfig: ModalConfig,
    ) {

        this.runtimeTestServiceStatus = new Observable<RuntimeCheck>(observer => {
            this._runtimeTestServiceStatusObserver = observer;
        }).pipe(share());

        this.runtimeTestServiceStatus.subscribe((event) => {
            // during setup we accumulate status to TEST_READY

            this.currentTestStatus += event;
            if (this.currentTestStatus === RuntimeCheck.TEST_READY) {
                this.startRecognitionTest();
            }

        });

    }

    get status():string {
        return this.statusString;
    }

    public runTest():void {
        this.reset();
        this.initializeRecognitionTest();
    }

    public startRecognitionTest():void {

        this.testAudioList = Object.keys(this.actualSentenceDataSet);
        this.log.log("Starting RUNTIME tests: Sentence data:" + this.testAudioList.length
            + " Reference data: " + Object.keys(this.recordedSentenceDataSet).length
                     + " dictionary: " + this.dictionary);

        this.mockMicrophoneService =
            new MockMicrophoneService(this.audioFormat, this.clientConfiguration, this.eventBus,
                this.browserAgent, this.log, this.modalConfig, "../readingassistant/resources/audioRecorderWorker.js");
        this.mockMicrophoneService.microphoneStatus.subscribe((status) => {
            if (status === MicrophoneStatus.FILE_READ_COMPLETE) {
                this.start = new Date();
                this.audioSec += this.mockMicrophoneService.getAudioLengthSec();
                console.log("starting record");
                this.recordingService.recordSentence(this.currentTestItem.sentence);

            }
        });

        let mockRecognizerService:RecognizerService = new RecognizerService(this.mockMicrophoneService,
            this.eventBus, this.log);
        // TODO: don't think these really need to be mocks.
        let testRecordingComparisonService:RecordingComparisonService = new RecordingComparisonService(this.log);

        this.referenceRecordingComparisonService = new RecordingComparisonService(this.log);

        let mockMicrophoneCheck = new MicrophoneCheckData();
        mockMicrophoneCheck.isMicrophoneCheck = false;
        mockMicrophoneCheck.microphoneCheckInitiated = true;

        this.contentCursor = new ContentCursor();
        this.studentConfig = new StudentConfiguration();
        this.studentConfig.pronunciationCorrectness = 50;

        this.recordingService = new RecordingService(
            mockRecognizerService,
            testRecordingComparisonService,
            this.sessionService,
            this.log,
            this.eventBus,
            this.contentCursor,
            null,
            null,
            null,
            this.studentConfig,
            this.modalConfig
        );

        this.recordingService.setMicrophoneCheckData(mockMicrophoneCheck);

        this.recordingService.sentenceFinishedCallback = (result:IRecordingComparisonResult) => {
            console.log("finished one file processing: " + this.currentTestItem.audioFile);
            // todo: store wordId result for later processing
            this.results[this.currentTestItem.audioFile] = {testResult: result.id, referenceResult: "",
                                                            numCorrectWords:0,
                                                            numFalsePositiveWords:0,
                                                            numFalseNegativeWords:0,
                                                            numTrueNegativeWords:0,
                                                            testResultDetail: result.resultDetail,
                                                            referenceResultDetail: null};
            let now = new Date();
            this.processingSec += (now.getTime() - this.start.getTime()) / 1000;
            if (this.testAudioList.length > 0) {
                this.runNextSentence();
                console.log("Running next file here");
            } else {
                console.log("Done running files, stopping test");
                this.compareResults();
                // TODO: analyze and compare results in detail
                this.summarizeTest();
                this._runtimeTestServiceStatusObserver.next(RuntimeCheck.TEST_FINISHED_ALL);
            }
        };
        mockRecognizerService.initializationObservable.subscribe(data => {
            this.log.log("Runtime Service: recognizer initialized: " + data);
            this.recordingService.initPronunciationDictionary(this.dictionary).subscribe((success) => {
                this.log.log("Runtime service: pronunciation dictionary initialized");
                this.runNextSentence();

            });
        });
        mockRecognizerService.init(this.studentConfig.pronunciationCorrectness);

    }

    private runNextSentence(): void {
        this.log.log("running next sentence");
        // TODO: make the audio file list a queue that we pop off of each run.
        let audioFile: string = this.testAudioList.shift();
        this.currentTestItem = {sentence: this.actualSentenceDataSet[audioFile], audioFile: audioFile};
        this.mockMicrophoneService.closeAudioContext();
        this.runOneSentence(this.currentTestItem);
    }

    private compareResults():void {
        let audioFileList = Object.keys(this.results);
        for (let index in audioFileList) {
            if (audioFileList.hasOwnProperty(index)) {
                let audioFile = audioFileList[index];
                this.log.log("audioFile: " + audioFile);
                this.referenceRecordingComparisonService.createSentence(this.actualSentenceDataSet[audioFile],
                                                                        this.dictionary);
                let result:IRecordingComparisonResult =
                    this.referenceRecordingComparisonService.getComparisonResult(
                        this.recordedSentenceDataSet[audioFile]);
                this.results[audioFile].referenceResult = result.id;
                this.results[audioFile].referenceResultDetail = result.resultDetail;
                this.log.log("test result for: " + audioFile + ": test result=" + this.results[audioFile].testResult
                             + ", reference result=" + this.results[audioFile].referenceResult);
            }
        }
    }

    private summarizeTest():void {
        let audioFileList  = Object.keys(this.results);
        let totalSentences:number = audioFileList.length;

        // collect statistics
        for (let index in audioFileList) {
            if (audioFileList.hasOwnProperty(index)) {
                let result:IResult = this.results[audioFileList[index]];
                result.numCorrectWords = this.getCorrectWords(result);
                result.numFalsePositiveWords = this.getFalsePositiveWords(result);
                result.numFalseNegativeWords = this.getFalseNegativeWords(result);
                let testSentence:MockCssSentence = this.actualSentenceDataSet[audioFileList[index]];
                let referenceSequence:IRecognizedSequence = this.recordedSentenceDataSet[audioFileList[index]];
                result.numTrueNegativeWords = this.getTrueNegativeWords(result, testSentence, referenceSequence);
            }
        }
        // summarize statistics
        let correctSentenceCount = 0;
        let totalUserCorrectWords = 0;
        let totalUserErrorWords = 0;
        let numFalsePositiveWords = 0;
        let numFalseNegativeWords = 0;

        for (let index in audioFileList) {
            if (audioFileList.hasOwnProperty(index)) {
                let result:IResult = this.results[audioFileList[index]];
                totalUserCorrectWords += result.numCorrectWords + result.numFalseNegativeWords;
                totalUserErrorWords += result.numFalsePositiveWords + result.numTrueNegativeWords;
                numFalsePositiveWords += result.numFalsePositiveWords;
                numFalseNegativeWords += result.numFalseNegativeWords;
                if (result.testResult === result.referenceResult) {
                    correctSentenceCount++;
                }
            }
        }
        this.statusString = "";
        let percentage:number = 100 * numFalseNegativeWords / totalUserCorrectWords;
        let resultString:string = "Total false negatives: " + numFalseNegativeWords + " out of: "
                                  + totalUserCorrectWords + " percentage: " + Number(percentage).toFixed(2);
        this.statusString += resultString + "\n";
        percentage = 100 * numFalsePositiveWords / totalUserErrorWords;
        resultString = "Total false positives: " + numFalsePositiveWords + " out of: " + totalUserErrorWords
            + " percentage: " + Number(percentage).toFixed(2);
        this.statusString += resultString + "\n";

        resultString = "Ran " + totalSentences + " sentences with "
            + Number(this.audioSec).toFixed(2) + " seconds of audio, processing took "
                       + Number(this.processingSec).toFixed(2) + " seconds.";
        this.statusString += resultString + "\n";
        this.log.log(this.statusString);
    }

    private getFalseNegativeWords(result:IResult):number {
        let referenceMatched:number = this.getNumMatchedUpToEnd(result.referenceResultDetail, result.referenceResult);

        let testMatched:number = this.getNumMatchedUpToEnd(result.testResultDetail, result.testResult);
        if (testMatched < referenceMatched) {
            return 1;
        }

        return 0;

    }
    // todo: current implementation stops at first false pos but there could be > 1
    private getFalsePositiveWords(result:IResult):number {
        let referenceMatched:number = this.getNumMatchedUpToEnd(result.referenceResultDetail, result.referenceResult);

        let testMatched:number = this.getNumMatchedUpToEnd(result.testResultDetail, result.testResult);
        if (testMatched > referenceMatched) {
            return (testMatched - referenceMatched);
        }

        return 0;

    }

    private getCorrectWords(result:IResult):number {
        let referenceMatched:number = this.getNumMatchedUpToEnd(result.referenceResultDetail, result.referenceResult);

        let testMatched:number = this.getNumMatchedUpToEnd(result.testResultDetail, result.testResult);
        let correct:number = Math.min(referenceMatched, testMatched);
        return correct;

    }

    // identifying true negative words is tricky.  All the following must be true:
    // the reference and test transcriptions ended on the same word
    // it is not the last word of the sentence
    // the user said something in the transcription *after* the word the reference and test transcriptions ended on.
    private getTrueNegativeWords(result:IResult, testSentence:MockCssSentence,
                                 referenceSequence:IRecognizedSequence):number {
        if (result.testResult !== result.referenceResult) {
            return 0;
        }
        let lastWordId = testSentence.words[testSentence.words.length - 1].id;
        if (result.testResult === lastWordId) {
            return 0;
        }

        // TODO: if we are using word-ids/multi-word, does this break?  Probably doesn't matter for current test sets

        if (!result.testResult) { // no matched words; if user said anything then it is a true negative
            return (referenceSequence.data.length > 0) ? 1 : 0;
        } else {
            return this.hasTrueNegativeWord(result, referenceSequence) ? 1 : 0;
        }

    }

    private hasTrueNegativeWord(result:IResult, referenceSequence:IRecognizedSequence):boolean {
    // find the last matched word id and corresponding time info in the reference result detail
        let pathItem:PathElementData = null;
        let hasTrueNegative = false;
        for (let i = 0; i < result.referenceResultDetail.length; i++) {
            if (result.referenceResultDetail[i].textWordId === result.testResult) {
                pathItem = result.referenceResultDetail[i];
                break;
            }
        }

        if (pathItem) {
            for (let i = 0; i < referenceSequence.data.length; i++) {
                // find the matching time info in referenceData.sentence.
                // If it is the last entry, return 0, else return 1
                if (pathItem.recognitionEndMSec === referenceSequence.data[i].end) {
                    if (i === referenceSequence.data.length - 1) {
                        hasTrueNegative = false;
                    } else {
                        hasTrueNegative = true;
                    }
                }
            }
        }
        return hasTrueNegative;
    }

    private getNumMatchedUpToEnd(resultDetail:PathElementData[], endWordId:string):number {
        let matched = 0;
        for (let i = 0; i < resultDetail.length; i++) {
            if (resultDetail[i].textWordId) {
                matched++;
            }
            if (resultDetail[i].textWordId === endWordId) {
                break;
            }
        }
        return matched;

    }

    private runOneSentence(testData:ITestData):void {

        let metaData:IMetaData = <IMetaData>{};
        let selection = new CssSelection({questionEntries: [], metaData: metaData,
            text: [{sentences: [testData.sentence]}]});
        testData.sentence.pronunciationDictionary = this.dictionary;
        this.contentCursor.init(selection);
        this.contentCursor.selectSentence(testData.sentence);
        // this.recordingService.recordSentence(testData.testSentence);
        // moved; now we call recordSentence after the file read is complete
        this.mockMicrophoneService.requestFile(testData.audioFile);
    }

    public reset():void {
        this.currentTestStatus = 0;
        this.actualSentenceDataSet = {}; // the sentence user was reading
        this.recordedSentenceDataSet = {}; // what they actually said
        this.dictionary = {};
        this.results = {};
    }

    public initializeRecognitionTest():void {
        this.log.log("Initializing runtime tests...");
        // this.fetchTest("test.all").subscribe((data) => {
        this.fetchTest("test.allfposhalf").subscribe((data:any) => {
            this.log.log("test data:" + data);
            let testId = data.TestSet;
            this._runtimeTestServiceStatusObserver.next(RuntimeCheck.TEST_METADATA);

            this.fetchDictionary("test").subscribe((dictionaryData:any) => {
                this.log.log("dictionary data:" + dictionaryData);
                this.dictionary = this.buildDictionary(dictionaryData);
                this._runtimeTestServiceStatusObserver.next(RuntimeCheck.TEST_DICTIONARY);
            });

            this.fetchSentenceData("storysentence" + testId).subscribe((sentence:any) => {

                let grammarLines = sentence.split("\n");
                for (let grammarLine in grammarLines) {
                    if (grammarLines[grammarLine].length > 0) {
                        let testData = this.buildGrammarData(grammarLines[grammarLine]);
                        this.actualSentenceDataSet[testData.audioFile] = testData.sentence;
                    }
                }
                this._runtimeTestServiceStatusObserver.next(RuntimeCheck.TEST_SENTENCE);
            });

            this.fetchSentenceData("referencetrans" + testId).subscribe((sentence:any) => {
                this.log.log("reference sentence:" + sentence);
                let transcriptLines = sentence.split("\n");
                for (let transcriptLine in transcriptLines) {
                    if (transcriptLines[transcriptLine].length > 0) {
                        let testData = this.buildMockRecognizedSequence(transcriptLines[transcriptLine]);
                        this.recordedSentenceDataSet[testData.audioFile] = testData.sentence;
                    }
                }
                this._runtimeTestServiceStatusObserver.next(RuntimeCheck.TEST_REFERENCE);
            });
        });
    }

    public stopTest():void {
        this.recordingService.pause();
    }

    public buildDictionary(data:string): IExportDictionary {
        let dict:{ [wordKey: string]: Pronunciation[]; } = {};
        let dictLines = data.split("\n");
        dictLines.forEach((dictLine) => {
            let wordpronpair = dictLine.split(/ (.+)/);
            let word = wordpronpair[0].replace(/\(\d+\)/, "");
            if (dictLine !== "") { // skip empty lines
                if (!dict[word]) {
                    dict[word] = [wordpronpair[1]];
                } else {
                    dict[word].push(wordpronpair[1]);
                }
            }
        });
        return dict;
    }

    public buildGrammarData(grammarLine:string):ITestData {
        let testData = {sentence: null, audioFile: ""};
        let audioArray = grammarLine.match(/\(([^)]+)\)/);
        let audioFile = "";
        if (audioArray && audioArray.length > 0) {
            audioFile = audioArray[1].replace("test/", this.TEST_CONTENT_PATH + this.automatedPath);
        }
        grammarLine = grammarLine.replace(/ [/]/g, "/");
        let grammarWords = grammarLine.split(/[ }{@]+/);
        testData.sentence = this.buildCssSentence(grammarWords);
        testData.audioFile = audioFile;
        return testData;
    }

    public buildCssSentence(testData:any):MockCssSentence {
        let sentence:MockCssSentence = new MockCssSentence({words: [], audio: ""}, null);
        for (let entryIndex in testData) {
            if (testData.hasOwnProperty(entryIndex)) {
                let entry = testData[entryIndex];
                if (entry !== "" && entry.indexOf("(") < 0) {
                    if (entry.indexOf("/") > -1) { // glue word notation
                        let word = entry.split("/")[0].toUpperCase();
                        sentence.words.push(
                            new CssWord({prefix: "", word: word, category: "1", audio: "", separator: " "}, sentence));
                    } else {
                        let word = entry.toUpperCase();
                        sentence.words.push(
                            new CssWord({prefix: "", word: word, category: "2", audio: "", separator: " "}, sentence));
                    }
                }
            }
        }
        return sentence;
    }

    public buildMockRecognizedSequence(transcriptLine:string):ITestData {
        let truthData = {sentence: null, audioFile: ""};
        let audioArray = transcriptLine.match(/\(([^)]+)\)/);
        let audioFile = "";
        if (audioArray && audioArray.length > 0) {
            audioFile = audioArray[1].replace("test/", this.TEST_CONTENT_PATH + this.automatedPath);
        }
        let transcriptWords = transcriptLine.split(/[ \t]+/);
        truthData.sentence = this.buildRecognizedSequence(transcriptWords);
        truthData.audioFile = audioFile;
        return truthData;
    }

    public buildRecognizedSequence(transcriptData:any):IRecognizedSequence {
        let sentence:IRecognizedSequence = {data: [], endOfFile: true};
        // start and end times are unknown in fact but put in arbitrary increasing values so recording-comparison logic
        // will work correctly
        let start = 0;
        for (let entryIndex in transcriptData) {
            if (transcriptData.hasOwnProperty(entryIndex)) {
                let entry = transcriptData[entryIndex];
                if (entry !== "" && entry.indexOf("(") < 0) {
                    sentence.data.push({word: entry.toUpperCase(), start: start, end: start + 100, isWord: true});
                    start += 100;
                }
            }
        }
        this.log.log("sentence mock recognizer data: " + JSON.stringify(sentence));
        return sentence;
    }

    // TODO: replace http with window.fetch for jasmine/unit tests.
    public fetchDictionary(name:string):Observable<any> {
        const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
        let path = this.TEST_CONTENT_PATH + this.testPath + name + ".dict";
        return this.http.get(path, { headers, responseType: 'text'}) as Observable<any>;
    }

    public fetchTest(name:string):Observable<Response> {
        let path = this.TEST_CONTENT_PATH + this.testPath + name + ".json";
        return this.http.get(path) as Observable<Response>;
    }

    public fetchSentenceData(name:string):Observable<any> {
        const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
        let path = this.TEST_CONTENT_PATH + this.testPath + name + ".txt";
        return this.http.get(path, { headers, responseType: 'text'}) as Observable<any>;
    }
}
